/**
 * events-public-data — the typed seam for the Event List Page and Event
 * Landing Page journeys, the events counterpart of `appointment-data.ts`.
 *
 * Everything here goes through the real, source-stable contracts:
 *   - `getPublicEvents` / `getPublicEvent` / `submitPublicEventRegistration`
 *     (`@v0y4ge/api-client` events door) for the anonymous catalogue, detail
 *     and registration.
 *   - `billing.getEventPublicPaymentConfig` for whether a paid tier can even
 *     be offered before any card control renders.
 *   - `publishing.downloadPublicEventCalendar` for the signed per-registration
 *     .ics — never a hand-built calendar file and never the management URL's
 *     token used as if it were the calendar token.
 *   - `getPortalPage('events')` for the PUBLIC, live-only settings a visitor
 *     may see (presentation + the authored `events` block), and
 *     `getPortalExperiencePage` / `putPortalExperiencePage` /
 *     `publishPortalExperiencePage` for the STUDIO authoring side of that same
 *     document (draft read/write, explicit publish).
 *   - `getLandingPage` / `createLandingPage` / `updateLandingPage` /
 *     `publishLandingPage` for the tenant-default and per-event landing
 *     documents, keyed by `EVENT_LANDING_DEFAULT_KEY` for the tenant default.
 *
 * No localStorage fallback, no alljourneys-api page-designer stub, and no
 * invented success: every "saved" or "published" state below is the server's
 * word for it, and every miss (404, absent config) resolves to an explicit,
 * neutral value the caller can render honestly rather than to a thrown
 * generic error or a silently fabricated default.
 */
import {
  getPublicEvents,
  getPublicEvent,
  submitPublicEventRegistration,
  getPortalPage,
  getPortalExperiencePage,
  putPortalExperiencePage,
  publishPortalExperiencePage,
  getLandingPage,
  createLandingPage,
  updateLandingPage,
  publishLandingPage,
  billing,
  publishing,
  newIdempotencyKey,
  type PublicEventListParams,
  type EventListResponse as PublicEventsListResponse,
  type PublicEventDetail as PublicEventDetailProjectionType,
  type PublicEventRegistrationInput,
  type PublicEventRegistrationResult,
  type IdempotentCallOptions,
} from "@v0y4ge/api-client";
import type { StudioLandingPagesRequestOptions } from "@v0y4ge/api-client";
import {
  EVENT_LIST_PRESENTATION_DEFAULTS,
  EVENT_LANDING_PRESENTATION_DEFAULTS,
  EVENT_LANDING_DEFAULT_KEY,
  resolveEventLandingPresentation,
  type EventListPresentation,
  type EventLandingPresentation,
} from "@v0y4ge/api-contracts";
import type {
  PortalExperiencePagePutInput,
  PortalExperiencePageResponse,
  PortalExperiencePageView,
  PortalExperienceBlock,
  LandingPageSaveInput,
  LandingPagePatchInput,
  EventPublicPaymentConfigResponse,
  LandingPageResponse,
} from "@v0y4ge/api-contracts";
import { currencyMinorUnits } from "@v0y4ge/jrni-ui";
import { studioRequestOptions } from "./studio-config";
import { friendlyDateShort, friendlyTime, tzNameLabel } from "./datetime";
import { t } from "./i18n";

/** The public route's product slug. Reserved: a tenant cannot rename hosting
 * for this route, so the events list is always authored/read at this slug. */
export const EVENTS_PAGE_SLUG = "events";

/** The registered One-Stop block type this document carries. Not `events-list`
 * — there is one registered type (`events`), matching the block registry. */
export const EVENTS_BLOCK_TYPE = "events";

export type PublicEventRow = PublicEventsListResponse["data"][number];
export type PublicEventDetail = PublicEventDetailProjectionType;

function isNotFound(err: unknown): boolean {
  const status = (err as { status?: number } | undefined)?.status;
  return status === 404;
}

/** 503 NOT_IMPLEMENTED: the tenant has no portal service composed (an events-only
 * tenant), so no events-page document can exist. The same honest miss as a 404
 * (CC, 2026-10-03). Any other 5xx is a real failure and still throws. */
function isNoPortalService(err: unknown): boolean {
  const e = err as { status?: number; code?: string } | undefined;
  return e?.status === 503 && e?.code === "NOT_IMPLEMENTED";
}

/* ── Public list + detail reads ─────────────────────────────────────────── */

/** List params the Designer's list UI actually offers, mapped onto the wire
 * query the events read door accepts. Filtering, sorting and paging all
 * happen server-side; the caller must not re-page or re-sort the page it
 * gets back. */
export interface PublicEventsQuery {
  page?: number;
  limit?: number;
  includeNonBookable?: boolean;
  includeSoldOut?: boolean;
  upcoming?: boolean;
  from?: string;
  to?: string;
  locationId?: string;
  category?: string;
  sort?: "date" | "name" | "popularity";
  /** Free-text search — `getPublicEvents`'s `q` param. */
  query?: string;
}

export function loadPublicEventsList(query: PublicEventsQuery = {}): Promise<PublicEventsListResponse> {
  const params: PublicEventListParams = {
    page: query.page,
    limit: query.limit,
    includeNonBookable: query.includeNonBookable,
    includeSoldOut: query.includeSoldOut,
    upcoming: query.upcoming,
    from: query.from,
    to: query.to,
    locationId: query.locationId,
    category: query.category,
    sort: query.sort,
    query: query.query,
  };
  return getPublicEvents(params);
}

/** A single event by its opaque public id (e.g. `cu-event-04`) — no UUID
 * validation, since the id is opaque by contract. */
export function loadPublicEventDetail(eventId: string): Promise<PublicEventDetail> {
  return getPublicEvent(eventId);
}

/* ── Registration ────────────────────────────────────────────────────────── */

/** Submit a public registration with a caller-supplied idempotency key (mint
 * with `newIdempotencyKey()` — no PII in the key itself). First/last/email
 * are mandatory regardless of the authored optional toggle — the current
 * JSON contract enforces that independent of any Designer setting. */
export function submitEventRegistration(
  input: PublicEventRegistrationInput,
  options?: IdempotentCallOptions,
): Promise<PublicEventRegistrationResult> {
  return submitPublicEventRegistration(input, options);
}

/**
 * Register as the SIGNED-IN customer, through the session this host established
 * when the One-Stop hand-off was redeemed. The body is the ticket choice and
 * the event's own answers; who is registering comes from the session, so no
 * identity travels here at all.
 */
export function registerPortalEventSession(
  journeySlug: string,
  // The ARRAY form the WYSIWYG registration form emits, matching the contract
  // and the anonymous event lane. A keyed record is the LANDING surface's
  // shape and is refused here.
  input: {
    /** The event being registered for; the door refuses any other. */
    event_id: string;
    ticket_type_id?: string;
    /** The signed-in checkout this registration buys (CC 0236). */
    checkout_id?: string;
    /** A paid ticket's payment (CC 0242), the anonymous lane's three fields and rules. */
    paymentIntentId?: string;
    orderAccessToken?: string;
    paymentCredentialProvider?: "stripe" | "paypal" | "adyen";
    answers?: { questionId: string; value: string | number | boolean | string[] | Record<string, number> | null }[];
    /** FILL-INS for what the account lacks. Sent only for a field the session
     *  supplied no value for; the server reads the account first and ignores
     *  these wherever the account already answers. */
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    phone_country?: string;
  },
  csrf: string,
): Promise<unknown> {
  return publishing.registerPortalEventSession(journeySlug, input as never, csrf, { baseUrl: getPublicEventsBaseUrl() });
}

export { newIdempotencyKey };

/** Signed in (CC 0236): the event's tickets with `eligible` for THIS customer, and their member-tier labels. */
export function loadPortalEventSessionTicketTiers(journeySlug: string): Promise<{ eventId: string; currency: string; tiers: { id: string; audience?: string; eligible: boolean; memberTierLabels: string[] }[] }> {
  return publishing.getPortalEventSessionTicketTiers(journeySlug, { baseUrl: getPublicEventsBaseUrl() }) as never;
}
/** Signed in (CC 0236): hold seats as the session's customer; complete with registerPortalEventSession + checkout_id. */
export function startPortalEventSessionCheckout(journeySlug: string, lines: { tierId: string; quantity: number }[], csrf: string, idempotencyKey: string): Promise<{ checkout: { checkoutId: string; totals?: { dueNowMinor?: number } } }> {
  return publishing.startPortalEventSessionCheckout(journeySlug, { lines }, csrf, { baseUrl: getPublicEventsBaseUrl(), idempotencyKey }) as never;
}

/** Whether — and how — a paid tier may actually be offered for this event.
 * `null` means no payment provider is configured; callers must disable paid
 * controls up front rather than hardcode a provider. Any error other than an
 * honest "no provider" (e.g. a 403 from the endpoint) is rethrown — a refused
 * read is not the same fact as "free", and must not be swallowed into it. */
export async function loadEventPaymentConfig(eventId: string): Promise<EventPublicPaymentConfigResponse> {
  return billing.getEventPublicPaymentConfig(eventId);
}

/** The signed per-registration calendar file. Requires the token issued with
 * the registration's ticket links — never the management-URL token, and
 * never fabricated client-side. */
export function downloadEventCalendar(eventId: string, calendarToken: string): Promise<string> {
  return publishing.downloadPublicEventCalendar(eventId, calendarToken, { baseUrl: getPublicEventsBaseUrl() });
}

function getPublicEventsBaseUrl(): string {
  try {
    return (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ || "";
  } catch {
    return "";
  }
}

/* ── Price + availability projection ─────────────────────────────────────── */

/**
 * `priceFrom: null` is UNKNOWN pricing (the projection never returns a
 * confident number for it — e.g. mixed-currency tiers), NOT "Free". A known
 * amount of exactly 0 minor units IS free. Currency-exponent aware: JPY (0
 * decimal places), KWD/BHD/OMR (3) and every ISO-4217 default (2) via the
 * shared `currencyMinorUnits` — never an unconditional `/100`.
 */
export function formatEventPrice(priceFrom: { amountMinor: number; currency: string | null } | null): string {
  if (!priceFrom) return "Price unavailable";
  if (priceFrom.amountMinor === 0) return "Free";
  const currency = priceFrom.currency;
  // A positive amount with no currency cannot be rendered as a number of
  // anything — that is unknown pricing, not a raw minor-unit count.
  if (!currency) return "Price unavailable";
  const exponent = currencyMinorUnits(currency);
  const major = priceFrom.amountMinor / Math.pow(10, exponent);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: exponent, maximumFractionDigits: exponent }).format(major);
  } catch {
    return `${major.toFixed(exponent)} ${currency}`;
  }
}

/**
 * Separate, real availability facts — never collapsed into one "closed"
 * bucket. `bookable=false` can mean any of `bookable_reason`'s values
 * (completed/cancelled/registration_closed/sold_out/waitlist_full), not
 * necessarily sold out; sold-out and waitlist-open are their own server
 * fields and must be read as such.
 */
export interface EventAvailabilitySummary {
  bookable: boolean;
  bookableReason: PublicEventRow["bookable_reason"];
  registrationOpen: boolean;
  soldOut: boolean;
  waitlistOpen: boolean;
  /** `null` = unbounded, never rendered as a number and never as "0 left". */
  spotsRemaining: number | null;
  waitlistSpotsRemaining: number | null;
  /** The event is in progress now: the server's own status is "live" (start <= now < end, in the
   *  event's zone). A public door no longer registers for it or takes a waitlist join (CC 0362). */
  happeningNow: boolean;
}

export function projectAvailability(row: PublicEventRow): EventAvailabilitySummary {
  return {
    bookable: row.bookable,
    bookableReason: row.bookable_reason,
    registrationOpen: row.registrationOpen,
    soldOut: row.soldOut,
    waitlistOpen: row.waitlistOpen,
    spotsRemaining: row.spots_remaining,
    waitlistSpotsRemaining: row.waitlist_spots_remaining,
    happeningNow: row.status === "live",
  };
}

/** "3 spots left" / "Open" (unbounded) / "Sold out" / "Waitlist open" — never
 * "Infinity", never "0 left" for a null (unbounded) cap. */
export function formatSpotsRemaining(spotsRemaining: number | null, soldOut: boolean): string {
  if (soldOut) return "Sold out";
  if (spotsRemaining == null) return "Open";
  return `${spotsRemaining} spot${spotsRemaining === 1 ? "" : "s"} left`;
}

/* ── Wall-clock formatting ────────────────────────────────────────────────
 * `start_at`/`end_at` (and the legacy `start_utc`/`end_utc` aliases, which are
 * NOT reliably UTC by name) carry EITHER shape, and the two need opposite
 * handling — this is the exact predicate `workers/events/src/event-time.ts`
 * uses to decide which:
 *   - a trailing `Z` or a numeric `+HH:MM`/`-HHMM` offset means the string is
 *     an ABSOLUTE instant. It must be CONVERTED into the event's own
 *     `timezone` for display (the same instant reads as a different local
 *     time in every zone — 2026-09-16T22:00:00Z is Sep 16, 6pm in
 *     America/New_York and Sep 17, 7am in Asia/Tokyo).
 *   - no offset at all means a NAIVE wall clock already expressed in
 *     `timezone` — e.g. "2026-03-05T14:30:00" literally means 14:30 there,
 *     not an instant to convert. Formatting this WITHOUT `timeZone` (via a
 *     UTC-tagged `Date` whose fields equal the stored digits verbatim, then
 *     formatted with `timeZone: "UTC"` so Intl performs zero conversion)
 *     avoids double-applying an offset that was never present.
 * Every formatter below takes `timezone` and dispatches on this predicate;
 * none may silently drop a real offset it was given.
 */
const ABSOLUTE_INSTANT_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

function parseNaiveWallClock(iso: string): { y: number; mo: number; d: number; h: number; mi: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]) - 1, d: Number(m[3]), h: Number(m[4]), mi: Number(m[5]) };
}

/** Resolve a stored `start_at`/`end_at` string to the `{ date, timeZone }`
 * pair `Intl.DateTimeFormat` should actually use: the real absolute instant
 * with the event's zone (converts), or a UTC-tagged stand-in for the naive
 * wall-clock digits with `timeZone: "UTC"` (does not convert). */
function resolveDisplayInstant(iso: string, timezone: string): { date: Date; timeZone: string } | null {
  const raw = String(iso || "").trim();
  if (!raw) return null;
  if (ABSOLUTE_INSTANT_RE.test(raw)) {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return { date: d, timeZone: timezone || "UTC" };
  }
  const p = parseNaiveWallClock(raw);
  if (!p) return null;
  return { date: new Date(Date.UTC(p.y, p.mo, p.d, p.h, p.mi)), timeZone: "UTC" };
}

export function formatEventWallClockDate(iso: string, timezone: string): string {
  const resolved = resolveDisplayInstant(iso, timezone);
  if (!resolved) return "";
  try {
    // The workspace's date pattern when one is set (item 8), else the locale's.
    return friendlyDateShort(resolved.date, resolved.timeZone) || new Intl.DateTimeFormat(undefined, { timeZone: resolved.timeZone, month: "short", day: "numeric", year: "numeric" }).format(resolved.date);
  } catch {
    return "";
  }
}

export function formatEventWallClockTime(iso: string, timezone: string): string {
  const resolved = resolveDisplayInstant(iso, timezone);
  if (!resolved) return "";
  try {
    // The workspace's clock (12h/24h) through lib/datetime (item 8).
    return friendlyTime(resolved.date, resolved.timeZone) || new Intl.DateTimeFormat(undefined, { timeZone: resolved.timeZone, hour: "numeric", minute: "2-digit" }).format(resolved.date);
  } catch {
    return "";
  }
}

/** The real local calendar day (`YYYY-MM-DD`), IN THE EVENT'S OWN TIMEZONE,
 * for a stored `start_at`/`end_at` — for date-range filters, which must
 * compare real days, never a formatted display label. Uses
 * `Intl.DateTimeFormat`'s parts rather than `Date` getters so an absolute
 * instant near a zone's day boundary still lands on the correct local day. */
export function eventWallClockDayKey(iso: string, timezone: string): string {
  const resolved = resolveDisplayInstant(iso, timezone);
  if (!resolved) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: resolved.timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(resolved.date);
    const y = parts.find((p) => p.type === "year")?.value;
    const mo = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    return y && mo && d ? `${y}-${mo}-${d}` : "";
  } catch {
    return "";
  }
}

/** The civil calendar day (`YYYY-MM-DD`) a raw instant (epoch ms) falls on in
 * `timeZone` — the same conversion `workers/portal/src/resolve.ts`'s
 * `civilDate()` performs, for computing a relative `dateRange.horizonDays`/
 * `pastHorizonDays` window in the canonical resolver's OWN zone rather than
 * the visitor's browser zone. */
export function civilDateInZone(instantMs: number, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timeZone || "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(instantMs));
    const y = parts.find((p) => p.type === "year")?.value;
    const mo = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    return y && mo && d ? `${y}-${mo}-${d}` : "";
  } catch {
    return "";
  }
}

/* ── Public Events Page (slug "events") — anonymous read ───────────────────
 * Live settings only: `presentation` (page.presentation itself —
 * showHeader/showFooter are AUTHORITATIVE fields there, not on eventJourney)
 * and the authored `events` block's config, both resolved from the PUBLISHED
 * version. A 404 (no document authored/published yet) resolves to the
 * neutral platform defaults below, not to a thrown error and not to a
 * fabricated "published" state. */

export interface ResolvedEventsPageConfig {
  showHeader: boolean;
  showFooter: boolean;
  /** Branding header/footer instead of the basic chrome (2026-09-23). */
  brandedHeader?: boolean;
  brandedFooter?: boolean;
  /** The published document set brandedHeader / brandedFooter at all (CC, 2026-10-03).
   *  When it did not, public pages draw the tenant's Branding kit by default. */
  brandedHeaderStored?: boolean;
  brandedFooterStored?: boolean;
  /** The canonical resolver's own tenant display zone
   * (`PortalAnonymousDocumentResponse.timeZone`, `portal.ts`) — the zone
   * `windowFor`/`eventsWindowFor` (`workers/portal/src/resolve.ts`) actually
   * resolve `dateRange`/`pastHorizonDays` in. Never guessed locally. */
  timeZone: string;
  eventJourney: EventListPresentation;
  /** The authored `events` block's settings, semantically mapped (see
   * `EVENTS_BLOCK_SEMANTIC_MAP`). Absent block ⇒ neutral defaults below. */
  block: EventsBlockConfig;
  /** Whether a real document/block was found, vs. these being neutral
   * platform defaults for a tenant that has authored nothing yet. */
  isConfigured: boolean;
}

/** The subset of the registered `events` block's config this list journey
 * reads, using the SAME field names the registry declares (`packages/
 * api-contracts/src/canvas-one-stop-blocks.ts` `EventsPropsSchema`) — no
 * second spelling minted here. Extends `Record<string, unknown>` so a whole
 * block config (including fields this journey does not read — `scope`,
 * `dateRange`, `pastHorizonDays`, `showAllEventsLink`, `allEventsLabel`,
 * `ctaLabel`, `maxItems`) can be carried and merged without a cast that hides
 * an actual shape mismatch. */
export interface EventsBlockConfig extends Record<string, unknown> {
  pageSize: number;
  sort: "date" | "popularity" | "curated" | "name";
  order: "asc" | "desc";
  includePast: boolean;
  includeSoldOut: boolean;
  showVenue: boolean;
  showDescription: boolean;
  showRegistrationCounts: boolean;
  showWaitlistState: boolean;
  showFilterUi: boolean;
}

export const NEUTRAL_EVENTS_BLOCK_CONFIG: EventsBlockConfig = {
  pageSize: 20,
  sort: "date",
  order: "asc",
  includePast: false,
  includeSoldOut: true,
  showVenue: true,
  showDescription: true,
  showRegistrationCounts: true,
  showWaitlistState: true,
  showFilterUi: true,
};

/**
 * Designer-local field name → the registered `events` block's real config
 * key. The Designer's `s` state uses the shorter, editor-facing names; the
 * wire config (authored and read back) uses the registry's names. One table,
 * both directions, so the mapping cannot drift between save and read.
 */
export const EVENTS_BLOCK_SEMANTIC_MAP = {
  sortOrder: "sort",
  eventsPerPage: "pageSize",
  showPastEvents: "includePast",
  showSoldOut: "includeSoldOut",
  showEventVenue: "showVenue",
  showEventDesc: "showDescription",
  showRemaining: "showRegistrationCounts",
  waitlist: "showWaitlistState",
} as const;

/** Reverse of `EVENTS_BLOCK_SEMANTIC_MAP` — the registry key → the Designer
 * field name, for hydrating `state.s` from a loaded/read block config. */
export const EVENTS_BLOCK_SEMANTIC_MAP_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(EVENTS_BLOCK_SEMANTIC_MAP).map(([designerKey, blockKey]) => [blockKey, designerKey]),
);

/** Hydrate the Designer's `s`-shaped fields (`sortOrder`, `eventsPerPage`, …)
 * from a real, loaded block config — the inverse of
 * `eventsBlockConfigFromDesignerFields`. Un-mapped block keys (`scope`,
 * `dateRange`, …) are not Designer fields and are intentionally dropped here;
 * callers that need to preserve them keep the raw block separately. */
export function designerFieldsFromEventsBlockConfig(block: EventsBlockConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.keys(EVENTS_BLOCK_SEMANTIC_MAP_REVERSE).forEach((blockKey) => {
    if (blockKey in block) out[EVENTS_BLOCK_SEMANTIC_MAP_REVERSE[blockKey]] = block[blockKey];
  });
  return out;
}

function findEventsBlock(blocks: readonly { type: string; config: Record<string, unknown> }[] | undefined): { type: string; config: Record<string, unknown> } | null {
  return blocks?.find((b) => b.type === EVENTS_BLOCK_TYPE) || null;
}

function readEventsBlockConfig(block: { config: Record<string, unknown> } | null): EventsBlockConfig {
  if (!block) return { ...NEUTRAL_EVENTS_BLOCK_CONFIG };
  const cfg = block.config || {};
  return {
    ...NEUTRAL_EVENTS_BLOCK_CONFIG,
    ...cfg,
    pageSize: typeof cfg.pageSize === "number" ? cfg.pageSize : NEUTRAL_EVENTS_BLOCK_CONFIG.pageSize,
    sort: (cfg.sort as EventsBlockConfig["sort"]) || NEUTRAL_EVENTS_BLOCK_CONFIG.sort,
    order: (cfg.order as EventsBlockConfig["order"]) || NEUTRAL_EVENTS_BLOCK_CONFIG.order,
    includePast: typeof cfg.includePast === "boolean" ? cfg.includePast : NEUTRAL_EVENTS_BLOCK_CONFIG.includePast,
    includeSoldOut: typeof cfg.includeSoldOut === "boolean" ? cfg.includeSoldOut : NEUTRAL_EVENTS_BLOCK_CONFIG.includeSoldOut,
    showVenue: typeof cfg.showVenue === "boolean" ? cfg.showVenue : NEUTRAL_EVENTS_BLOCK_CONFIG.showVenue,
    showDescription: typeof cfg.showDescription === "boolean" ? cfg.showDescription : NEUTRAL_EVENTS_BLOCK_CONFIG.showDescription,
    showRegistrationCounts: typeof cfg.showRegistrationCounts === "boolean" ? cfg.showRegistrationCounts : NEUTRAL_EVENTS_BLOCK_CONFIG.showRegistrationCounts,
    showWaitlistState: typeof cfg.showWaitlistState === "boolean" ? cfg.showWaitlistState : NEUTRAL_EVENTS_BLOCK_CONFIG.showWaitlistState,
    showFilterUi: typeof cfg.showFilterUi === "boolean" ? cfg.showFilterUi : NEUTRAL_EVENTS_BLOCK_CONFIG.showFilterUi,
  };
}

/** Anonymous read of the public Events Page. Never used for Studio authoring
 * (that reads the draft through `loadEventsDocument` below, authenticated). */
export async function loadPublicEventsPage(): Promise<ResolvedEventsPageConfig> {
  try {
    const page = await getPortalPage(EVENTS_PAGE_SLUG);
    const eventJourney = page.presentation.eventJourney ? page.presentation.eventJourney : { ...EVENT_LIST_PRESENTATION_DEFAULTS };
    const eventsBlock = findEventsBlock(page.blocks as unknown as { type: string; config: Record<string, unknown> }[]);
    return {
      showHeader: page.presentation.showHeader,
      showFooter: page.presentation.showFooter,
      brandedHeader: page.presentation.brandedHeader === true,
      brandedFooter: page.presentation.brandedFooter === true,
      brandedHeaderStored: typeof page.presentation.brandedHeader === "boolean",
      brandedFooterStored: typeof page.presentation.brandedFooter === "boolean",
      timeZone: page.timeZone,
      eventJourney,
      block: readEventsBlockConfig(eventsBlock),
      isConfigured: Boolean(eventsBlock),
    };
  } catch (err) {
    // 404 (no published "events" document yet) is an honest, expected miss —
    // resolve to the platform defaults and the real catalogue, not an error.
    // So is 503 NOT_IMPLEMENTED: the tenant has no portal service at all.
    if (isNotFound(err) || isNoPortalService(err)) {
      return {
        showHeader: true,
        showFooter: true,
        timeZone: "UTC",
        eventJourney: { ...EVENT_LIST_PRESENTATION_DEFAULTS },
        block: { ...NEUTRAL_EVENTS_BLOCK_CONFIG },
        isConfigured: false,
      };
    }
    throw err;
  }
}

/* ── Studio authoring (draft read / put / publish) ──────────────────────────
 * Authenticated. On a missing document, `loadEventsDocument` returns a null
 * document (an honest "nothing authored yet"), never a document it silently
 * created — creation happens on the FIRST EXPLICIT SAVE, in
 * `saveEventsDocument`, matching the read/write split the contract documents.
 */

export interface EventsDocumentDraft {
  /** The exact loaded draft, verbatim — the baseline `saveEventsDocument`
   *  must PUT back with only the Designer-owned fields changed. `null` means
   *  nothing has been authored yet (create-on-first-save). */
  raw: PortalExperiencePageView | null;
  showHeader: boolean;
  showFooter: boolean;
  brandedHeader?: boolean;
  brandedFooter?: boolean;
  /** The stored draft already carries the branded keys (see saveEventsDocument). */
  brandedStored?: boolean;
  eventJourney: EventListPresentation;
  block: EventsBlockConfig;
  isPublished: boolean;
}

/** Read the tenant's draft Events document, or a draft with `raw: null` if
 * none has been saved yet. A network/permission failure is rethrown — that
 * is a real error, and callers must not treat it as "unconfigured" (doing so
 * risks a later save silently discarding real settings the read merely
 * failed to fetch). */
export async function loadEventsDocument(): Promise<EventsDocumentDraft> {
  const res = await getPortalExperiencePage(EVENTS_PAGE_SLUG).catch((err: unknown) => {
    if (isNotFound(err)) return null;
    throw err;
  });
  if (!res) {
    return {
      raw: null,
      showHeader: true,
      showFooter: true,
      eventJourney: { ...EVENT_LIST_PRESENTATION_DEFAULTS },
      block: { ...NEUTRAL_EVENTS_BLOCK_CONFIG },
      isPublished: false,
    };
  }
  const doc = res.data;
  const eventsBlock = findEventsBlock(doc.blocks as unknown as { type: string; config: Record<string, unknown> }[]);
  return {
    raw: doc,
    showHeader: doc.settings.showHeader,
    showFooter: doc.settings.showFooter,
    brandedHeader: doc.settings.brandedHeader === true,
    brandedFooter: doc.settings.brandedFooter === true,
    brandedStored: typeof doc.settings.brandedHeader === "boolean" || typeof doc.settings.brandedFooter === "boolean",
    eventJourney: doc.settings.eventJourney ? doc.settings.eventJourney : { ...EVENT_LIST_PRESENTATION_DEFAULTS },
    block: readEventsBlockConfig(eventsBlock),
    isPublished: doc.published,
  };
}

/**
 * Save (PUT) the Events document. Preserves the FULL loaded baseline
 * (`title`, `header`, `settings.navLabel`/`locale`/`supportedLocales`/
 * `layout`, and every OTHER authored block) and touches only:
 *   - `settings.showHeader` / `showFooter` / `eventJourney`
 *   - the `events` block's config, merged over its EXISTING config (so
 *     `scope`, `dateRange`, `pastHorizonDays`, `showAllEventsLink`,
 *     `allEventsLabel`, `ctaLabel` — any catalogue-scoping the block already
 *     carries — survive), with `maxItems` explicitly forced to `null` (this
 *     journey is always the paginated list, never the top-N preset) and the
 *     semantic-mapped fields overwritten from `patch`.
 * When `baseline.raw` is `null` (nothing authored yet), mints a valid minimal
 * document — locale `en`, a fresh `events` block — rather than guessing at
 * unrelated fields that do not exist yet.
 */
export function saveEventsDocument(baseline: EventsDocumentDraft, patch: {
  showHeader: boolean;
  showFooter: boolean;
  /** Present only when the author opted in or the draft already has them —
   *  the settings schema is strict on a server that predates the fields. */
  brandedHeader?: boolean;
  brandedFooter?: boolean;
  eventJourney: EventListPresentation;
  blockPatch: Partial<EventsBlockConfig>;
  timeZone: string;
}): Promise<PortalExperiencePageResponse> {
  const raw = baseline.raw;
  const existingBlocks: PortalExperienceBlock[] = raw ? [...raw.blocks] : [];
  const existingIndex = existingBlocks.findIndex((b) => b.type === EVENTS_BLOCK_TYPE);
  const existingBlock = existingIndex >= 0 ? existingBlocks[existingIndex] : null;
  const mergedConfig: EventsBlockConfig = {
    ...readEventsBlockConfig(existingBlock as { config: Record<string, unknown> } | null),
    ...patch.blockPatch,
    maxItems: null,
  };
  const nextBlock: PortalExperienceBlock = {
    id: existingBlock?.id || EVENTS_BLOCK_TYPE,
    type: EVENTS_BLOCK_TYPE,
    version: existingBlock?.version || 1,
    config: mergedConfig,
    ...(existingBlock?.visibility ? { visibility: existingBlock.visibility } : {}),
  };
  const blocks = existingIndex >= 0
    ? existingBlocks.map((b, i) => (i === existingIndex ? nextBlock : b))
    : [...existingBlocks, nextBlock];

  const body: PortalExperiencePagePutInput = {
    title: raw?.title || "Events",
    settings: {
      navLabel: raw?.settings.navLabel ?? null,
      locale: raw?.settings.locale || "en",
      supportedLocales: raw?.settings.supportedLocales || ["en"],
      timeZone: raw?.settings.timeZone || patch.timeZone,
      layout: raw?.settings.layout || "get-help-first",
      showHeader: patch.showHeader,
      showFooter: patch.showFooter,
      ...(typeof patch.brandedHeader === "boolean" ? { brandedHeader: patch.brandedHeader } : {}),
      ...(typeof patch.brandedFooter === "boolean" ? { brandedFooter: patch.brandedFooter } : {}),
      eventJourney: patch.eventJourney,
    },
    header: raw?.header || { eyebrow: null, title: null, subtitle: null },
    blocks,
  };
  return putPortalExperiencePage(EVENTS_PAGE_SLUG, body);
}

/** Explicit publish — the separate domain command that promotes the draft
 * just PUT to live. Never implied by a successful save. */
export function publishEventsDocument() {
  return publishPortalExperiencePage(EVENTS_PAGE_SLUG);
}

/* ── Landing authoring (per-event + tenant default) ─────────────────────────
 * Keyed by `EVENT_LANDING_DEFAULT_KEY` for the tenant-wide default template,
 * or by a real event id for a per-event override — `createLandingPage`'s
 * path is literally `{eventId}` (its client derives it from `input.eventId`),
 * so the reserved key travels in exactly that field: "with this value in the
 * path where an event id normally goes" (`pages.ts`'s own doc comment on the
 * key). Studio-only (authenticated); never called for the anonymous visitor,
 * who instead reads `landingDesign` off `getPublicEvent`. */

export interface LandingDraft {
  /** The path/`eventId` this document is addressed by — `EVENT_LANDING_DEFAULT_KEY`
   *  for the tenant default, or a real event id. `null` when nothing has been
   *  saved for this id yet (create-on-first-save). */
  id: string;
  raw: Record<string, unknown> | null;
  presentation: EventLandingPresentation;
  blocks: unknown[];
  isPublished: boolean;
}

/** `landingId` is `EVENT_LANDING_DEFAULT_KEY` for the tenant default, or a
 * specific event id for a per-event document. A network/permission failure
 * is rethrown, never folded into "nothing authored". */
export async function loadLandingDocument(landingId: string = EVENT_LANDING_DEFAULT_KEY): Promise<LandingDraft> {
  const res = await getLandingPage(landingId, studioRequestOptions("landing-pages") as StudioLandingPagesRequestOptions).catch((err: unknown) => {
    if (isNotFound(err)) return null;
    throw err;
  });
  if (!res) {
    return { id: landingId, raw: null, presentation: neutralLandingPresentationDefaults(), blocks: [], isPublished: false };
  }
  const data = ((res as unknown as { data?: Record<string, unknown> }).data ?? (res as unknown as Record<string, unknown>)) as Record<string, unknown>;
  return {
    id: String(data.id ?? landingId),
    raw: data,
    presentation: resolveEventLandingPresentation(data.presentation ?? null),
    blocks: Array.isArray(data.blocks) ? (data.blocks as unknown[]) : [],
    isPublished: data.status === "published",
  };
}

/** Create the landing document on first explicit save (no document existed
 * yet for this id). `eventId` is the reserved default key or a real event
 * id — the route the client derives from it is `POST /v1/landing/{eventId}`.
 * `slug` is REQUIRED by the contract even on create; it has no public meaning
 * for the reserved-default document (excluded from every public-by-slug
 * read), so it is simply set to `eventId` itself — already a valid kebab
 * token in both the reserved-key and real-event-id cases. */
export function createLandingDocument(eventId: string, presentation: EventLandingPresentation): Promise<LandingPageResponse> {
  const input: LandingPageSaveInput = { eventId, slug: eventId, presentation };
  return createLandingPage(input, studioRequestOptions("landing-pages") as StudioLandingPagesRequestOptions);
}

/** Update an existing landing document's presentation ONLY — omits
 * `blocks`/`theme`/`themeConfig`/`font`/`globalCss`/`brandKit`/`branding`
 * entirely so the PATCH's partial-update semantics leave them exactly as
 * they were, rather than this call round-tripping a possibly-stale local
 * copy back over them. */
export function updateLandingDocument(landingId: string, presentation: EventLandingPresentation): Promise<LandingPageResponse> {
  const input: LandingPagePatchInput = { presentation };
  return updateLandingPage(landingId, input, studioRequestOptions("landing-pages") as StudioLandingPagesRequestOptions);
}

/** Update an existing landing document's BLOCKS only — the mirror of
 * `updateLandingDocument`, and the write path for the custom layout canvas
 * (`screens/journeys/landing-custom-canvas`). `presentation`/`theme`/`font`/
 * `branding` are omitted so the PATCH's partial-update semantics leave them
 * as they were, exactly as the presentation-only patch leaves blocks alone.
 *
 * Geometry travels inside each block's `cfg` (a passthrough record on
 * `LandingBlock`), so this needs no contract change. Note that no PUBLIC
 * renderer reads these blocks yet — the write is real, the visitor-facing
 * render is not, and the authoring screen says so. */
export function updateLandingBlocks(landingId: string, blocks: LandingPageSaveInput['blocks']): Promise<LandingPageResponse> {
  const input: LandingPagePatchInput = { blocks };
  return updateLandingPage(landingId, input, studioRequestOptions("landing-pages") as StudioLandingPagesRequestOptions);
}

export function publishLandingDocument(landingId: string): Promise<LandingPageResponse> {
  return publishLandingPage(landingId, studioRequestOptions("landing-pages") as StudioLandingPagesRequestOptions);
}

/** Neutral defaults for an absent legacy landing presentation. Never used to
 * paper over a present-but-invalid value — `resolveEventLandingPresentation`
 * rejects those instead of silently substituting this. */
export function neutralLandingPresentationDefaults(): EventLandingPresentation {
  return { ...EVENT_LANDING_PRESENTATION_DEFAULTS };
}

/* ── Row projection for the existing Designer render tree ───────────────────
 * The Designer's event-array rendering (built against the removed fixtures)
 * reads a display-shaped row: id/name/cat/venue/date/time/price/left/maxPer/
 * cover/desc/closed/addr/tz. This maps the REAL wire projection onto exactly
 * those keys so that render tree runs unchanged against real data, rather
 * than being rewritten to a second shape. Availability fields are kept
 * SEPARATE (`soldOut`/`waitlistOpen`/`registrationOpen`/`bookableReason`) —
 * never collapsed into one `closed` boolean; `closed` here reflects only
 * `bookable`, and consumers must read the specific field for the specific
 * fact rather than infer "sold out" from "not bookable".
 *
 * `maxPer` is the event's own per-registration seat maximum, read from the
 * public attendee policy (`registrationQuantityBounds.max`, which counts the
 * host) and clamped the way the backend clamps it. It was pinned to 1 for as
 * long as the anonymous registration contract submitted one person per call
 * with no people list; `attendees` now exists end to end (CD docket
 * 2026-09-15 item 1), so the honest cap is the server's, not 1.
 */
export interface EventAttendeePolicy {
  /** May this registration carry anyone besides the host at all? */
  allowGuests: boolean;
  /** Maximum ADDITIONAL people, excluding the host. */
  maxGuests: number;
  /** Maximum seats in one registration, INCLUDING the host. */
  maxTotal: number;
}

/**
 * The public attendee policy for an event — server policy, never journey
 * config. The authored `s.attendees` switch can only ever narrow this; it is
 * not an authority, and the appointment side's `s.maxParty` is not one either.
 *
 * Read tolerantly and clamped exactly as the backend clamps (9 additional, 10
 * total), so an older stored row that is larger cannot widen the UI past what
 * the server will accept. Absent fields mean no guests: the contract defaults
 * `allowGuests` to false, and inferring permission from silence would offer a
 * visitor seats the registration is then rejected for.
 */
export function projectAttendeePolicy(row: PublicEventRow): EventAttendeePolicy {
  const r = row as unknown as {
    allowGuests?: unknown;
    maxGuestsPerRegistration?: unknown;
    registrationQuantityBounds?: { min?: unknown; max?: unknown } | null;
  };
  if (r.allowGuests !== true) return { allowGuests: false, maxGuests: 0, maxTotal: 1 };
  const int = (v: unknown, fallback: number) =>
    (typeof v === "number" && Number.isFinite(v) ? Math.floor(v) : fallback);
  const maxGuests = Math.max(0, Math.min(9, int(r.maxGuestsPerRegistration, 0)));
  const boundsMax = Math.max(1, Math.min(10, int(r.registrationQuantityBounds?.max, maxGuests + 1)));
  /* Two independent server facts that can disagree; the LOWER one is the one
     the backend will actually honour, since it derives requested seats as
     1 + attendees.length and rejects the whole registration atomically. */
  const maxTotal = Math.min(boundsMax, maxGuests + 1);
  return { allowGuests: maxTotal > 1, maxGuests: Math.max(0, Math.min(maxGuests, maxTotal - 1)), maxTotal };
}

export interface DesignerEventRow {
  id: string;
  name: string;
  cat: string;
  venue: string;
  date: string;
  time: string;
  /** "{date} · {time}" — the legacy fixture-row's combined display field,
   *  which several existing render sites (`curEvt.when`) still consume. */
  when: string;
  tz: string;
  dayKey: string;
  price: string;
  left: number | null;
  cap: number;
  maxPer: number;
  /** Server attendee policy for this event (CD docket 2026-09-15 item 1). */
  attendeePolicy: EventAttendeePolicy;
  cover: string;
  desc: string;
  closed: boolean;
  addr: string;
  hue: string;
  availability: EventAvailabilitySummary;
  publicPath: string;
  raw: PublicEventRow;
}

/** The lowest ticket price when the read sends no `priceFrom` (CC 0260): a v2 event can
 *  carry its prices only on its tiers. Every tier free → 0. A priced tier without a currency,
 *  or no tiers at all, stays unknown (null). */
/** The list row's price text: the server's priceFrom when it sent one, "" when it sent null
 *  on purpose, and the tiers only when the field is missing. */
function eventRowPrice(row: PublicEventRow): string {
  const sent = row as unknown as { priceFrom?: unknown; price_from?: unknown };
  if (sent.priceFrom !== undefined) return row.priceFrom === null ? "" : formatEventPrice(row.priceFrom);
  if (sent.price_from !== undefined) return row.price_from === null ? "" : formatEventPrice(row.price_from);
  return formatEventPrice(priceFromTiers(row));
}

function priceFromTiers(row: PublicEventRow): { amountMinor: number; currency: string | null } | null {
  /* Declared fields only (CC 0265): ticketTiers, else ticket_types. */
  const tiers: { priceMinor?: number; currency?: string | null }[] = row.ticketTiers && row.ticketTiers.length
    ? row.ticketTiers
    : (row.ticket_types || []).map((x) => ({ priceMinor: x.price_minor, currency: x.currency }));
  if (!tiers.length) return null;
  const known = tiers.filter((x) => typeof x.priceMinor === "number" && Number.isFinite(x.priceMinor));
  if (known.length !== tiers.length) return null;
  if (known.every((x) => x.priceMinor === 0)) return { amountMinor: 0, currency: known[0].currency ?? null };
  const paid = known.filter((x) => (x.priceMinor as number) > 0).sort((a, b) => (a.priceMinor as number) - (b.priceMinor as number));
  const lowest = known.some((x) => x.priceMinor === 0) ? { priceMinor: 0, currency: paid[0].currency } : paid[0];
  return { amountMinor: lowest.priceMinor as number, currency: lowest.currency ?? null };
}

export function projectPublicEventToDesignerRow(row: PublicEventRow, brandColor: string): DesignerEventRow {
  const availability = projectAvailability(row);
  const attendeePolicy = projectAttendeePolicy(row);
  const date = formatEventWallClockDate(row.start_at, row.timezone);
  const wallClock = formatEventWallClockTime(row.start_at, row.timezone);
  /* Name the event's own zone after its time, "5:30 PM (CDT)": the list card's "Show the time zone" switch strips the bracketed name. */
  const zone = wallClock ? tzNameLabel(row.timezone, row.start_at) : "";
  const time = zone ? `${wallClock} (${zone})` : wallClock;
  return {
    id: row.id,
    name: row.title,
    cat: row.category || "",
    /* An online event has no venue; read "Online" so the card and event page show a location. A real venue always wins. (CC, 2026-10-05) */
    venue: row.venue || row.location?.venue || (row.isVirtual || row.is_virtual ? t("journeys.newJourneys.venueOnline", "Online") : ""),
    date,
    time,
    when: time ? `${date} · ${time}` : date,
    tz: row.timezone,
    dayKey: eventWallClockDayKey(row.start_at, row.timezone),
    /* priceFrom null is the server saying "no single From price" (tiers in more than one
       currency): no price line (""). Only an ABSENT field falls back to the tiers (CC 0265). */
    price: eventRowPrice(row),
    left: availability.spotsRemaining,
    cap: row.capacity,
    maxPer: attendeePolicy.maxTotal,
    attendeePolicy,
    cover: row.imageUrl || "",
    desc: row.description || "",
    closed: !row.bookable,
    addr: row.locationAddress || "",
    hue: brandColor || "var(--jrni-color-primary-1, #5142C7)",
    availability,
    publicPath: row.public_path,
    raw: row,
  };
}

export { EVENT_LANDING_DEFAULT_KEY };
